import { useTranslation } from 'react-i18next';
import { Activity, Users, BookOpen, DollarSign, TrendingUp, AlertTriangle, Download, Upload, LogIn, LogOut, Edit, Plus, Trash, Loader } from 'lucide-react';
import { useStudentLabel } from '../../hooks/useStudentLabel';
import { formatDateOnly } from '../../utils/localeFormatters';

/**
 * RecentActivityList - Displays recent activities from audit logs
 * Shows last 30 activities with icons and timestamps
 */
export default function RecentActivityList({ activities = [], isLoading = false }) {
  const { t, i18n } = useTranslation();
  const { isTherapyCenter } = useStudentLabel();
  const isRTL = i18n.language === 'ar' || i18n.language === 'he';

  // Map icon strings to components
  const getIconComponent = (iconName) => {
    const iconMap = {
      'add': Plus,
      'visibility': TrendingUp,
      'edit': Edit,
      'delete': Trash,
      'download': Download,
      'upload': Upload,
      'login': LogIn,
      'logout': LogOut,
      'activity': Activity,
      'users': Users,
      'book': BookOpen,
      'dollar': DollarSign
    };
    return iconMap[iconName?.toLowerCase()] || Activity;
  };

  // Format relative time
  const getRelativeTime = (timestamp) => {
    if (!timestamp) return '';

    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffMs = now - activityTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return t('time.justNow');
    } else if (diffMins < 60) {
      return t('time.minutesAgo', { count: diffMins });
    } else if (diffHours < 24) {
      return t('time.hoursAgo', { count: diffHours });
    } else if (diffDays < 7) {
      return t('time.daysAgo', { count: diffDays });
    } else {
      return formatDateOnly(activityTime);
    }
  };

  // Build a localized activity description from the structured fields the
  // /audit-logs/recent endpoint exposes. Falls back to the raw `description`
  // / `action` strings the backend formats in English so older payloads keep
  // rendering the same way.
  const STANDALONE_ACTIONS = new Set(['login', 'logout', 'passwordChange']);
  // Resources that mean "student" in learning centers but "client" in
  // therapy / hair-transplant centers. Swapped via useStudentLabel.
  const STUDENT_LIKE_RESOURCES = new Set(['students', 'student']);

  const getResourceLabel = (resourceType) => {
    if (isTherapyCenter && STUDENT_LIKE_RESOURCES.has(resourceType)) {
      return t('common.client', { defaultValue: '' });
    }
    return t(`audit.resources.${resourceType}`, { defaultValue: '' });
  };

  const getLocalizedDescription = (activity) => {
    const actionType = activity.actionType;
    if (actionType) {
      const actionLabel = t(`audit.actions.${actionType}`, { defaultValue: '' });
      if (actionLabel) {
        if (STANDALONE_ACTIONS.has(actionType)) {
          return actionLabel;
        }
        const resourceType = activity.resourceType;
        if (resourceType) {
          const resourceLabel = getResourceLabel(resourceType);
          if (resourceLabel) {
            return t('audit.activityFormat', {
              defaultValue: '{{action}} {{resource}}',
              action: actionLabel,
              resource: resourceLabel,
            });
          }
        }
        return actionLabel;
      }
    }
    return activity.description || activity.action || t('dashboard.activity');
  };

  // Get activity color based on action
  const getActivityColor = (action) => {
    const colorMap = {
      'create': '#22c55e',
      'add': '#22c55e',
      'update': '#3b82f6',
      'edit': '#3b82f6',
      'delete': '#ef4444',
      'remove': '#ef4444',
      'login': '#8b5cf6',
      'logout': '#6b7280',
      'download': '#f59e0b',
      'upload': '#10b981'
    };

    const actionLower = action?.toLowerCase() || '';
    for (const key in colorMap) {
      if (actionLower.includes(key)) {
        return colorMap[key];
      }
    }
    return 'var(--color-primary-dark)';
  };

  if (isLoading) {
    return (
      <div
        style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: '12px',
          padding: '2rem',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          marginTop: '2rem',
          minHeight: '200px'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem' }}>
          <Loader size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-primary-dark)' }} />
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: 'var(--color-surface)',
        borderRadius: '12px',
        padding: '1.5rem',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        marginTop: '2rem',
        minHeight: '200px'
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <Activity size={20} color="var(--color-primary-dark)" />
        <h3 style={{
          fontSize: '1.125rem',
          fontWeight: '600',
          color: 'var(--color-text-primary)',
          margin: 0
        }}>
          {t('dashboard.recentActivity')}
        </h3>
        {activities.length > 0 && (
          <span style={{
            marginInlineStart: 'auto',
            fontSize: '0.875rem',
            fontWeight: '600',
            color: 'var(--color-primary-dark)',
            backgroundColor: 'var(--color-primary-light)',
            padding: '0.25rem 0.625rem',
            borderRadius: '12px'
          }}>
            {activities.length}
          </span>
        )}
      </div>

      {/* Activities List */}
      {activities.length === 0 ? (
        <div
          style={{
            padding: '3rem 1.5rem',
            textAlign: 'center',
            color: 'var(--color-text-secondary)'
          }}
        >
          <Activity size={48} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
          <p style={{ margin: 0, fontSize: '0.9375rem', fontWeight: '500' }}>
            {t('dashboard.noRecentActivity')}
          </p>
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.8125rem' }}>
            {t('dashboard.noActivityMessage')}
          </p>
        </div>
      ) : (
        <div
          style={{
            maxHeight: '600px',
            overflowY: 'auto',
            paddingRight: isRTL ? 0 : '0.5rem',
            paddingLeft: isRTL ? '0.5rem' : 0
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {activities.map((activity, index) => {
              const IconComponent = getIconComponent(activity.icon || activity.actionType || activity.action);
              const activityColor = getActivityColor(activity.actionType || activity.action);

              return (
                <div
                  key={activity.id || index}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem',
                    padding: '0.875rem',
                    backgroundColor: 'var(--color-hover)',
                    borderRadius: '8px',
                    border: '1px solid var(--color-border)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-surface-alt)';
                    e.currentTarget.style.borderColor = 'var(--color-border-dark)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-hover)';
                    e.currentTarget.style.borderColor = 'var(--color-border)';
                  }}
                >
                  {/* Icon */}
                  <div
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      backgroundColor: `${activityColor}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <IconComponent size={18} color={activityColor} />
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                      {/* Description */}
                      <div>
                        <div style={{
                          fontSize: '0.875rem',
                          color: 'var(--color-text-primary)',
                          marginBottom: '0.25rem',
                          lineHeight: '1.4'
                        }}>
                          {activity.userName && (
                            <span style={{ fontWeight: '600' }}>
                              {activity.userName}
                            </span>
                          )}
                          {activity.userName && ' '}
                          <span>{getLocalizedDescription(activity)}</span>
                        </div>

                        {/* Details */}
                        {activity.details && (
                          <div style={{
                            fontSize: '0.75rem',
                            color: 'var(--color-text-secondary)',
                            marginTop: '0.25rem',
                            fontStyle: 'italic'
                          }}>
                            {activity.details}
                          </div>
                        )}
                      </div>

                      {/* Timestamp */}
                      <div style={{
                        fontSize: '0.75rem',
                        color: 'var(--color-text-secondary)',
                        whiteSpace: 'nowrap',
                        flexShrink: 0
                      }}>
                        {getRelativeTime(activity.timestamp || activity.createdAt)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Custom scrollbar styles */}
      <style>{`
        div::-webkit-scrollbar {
          width: 6px;
        }
        div::-webkit-scrollbar-track {
          background: transparent;
        }
        div::-webkit-scrollbar-thumb {
          background: var(--color-border);
          border-radius: 3px;
        }
        div::-webkit-scrollbar-thumb:hover {
          background: var(--color-border-dark);
        }
      `}</style>
    </div>
  );
}
